import { Injectable, inject } from '@angular/core';
import {
  addTargetTolerance,
  isBoxInBox,
  isCenterInCircle,
  overlayCoordinates,
} from '@functions';
import { OverlayColor } from '@models';
import { CanvasService, DetectionService, RenderService } from '@services';
import '@tensorflow/tfjs-backend-webgl';

@Injectable({
  providedIn: 'root',
})
export class LogicService {
  private canvasService = inject(CanvasService);
  private detectionService = inject(DetectionService);
  private renderService = inject(RenderService);

  debounce: boolean[] = [];

  getDebounce(value: boolean, numberOfDebounce: number): boolean {
    if (this.debounce.length >= numberOfDebounce * 2) {
      this.debounce.pop();
    }
    this.debounce = [value, ...this.debounce];
    return (
      this.debounce.filter((entry) => entry).length >
      this.debounce.filter((entry) => !entry).length
    );
  }

  checkSamplePosition(data: {
    sample: string;
    workerId: string;
    confidence: number;
    width: number;
    height: number;
    moveX: number;
    moveY: number;
    image: string;
    additionalCondition?: boolean;
    tolerance?: { x: number; y: number };
  }): Promise<boolean> {
    return new Promise((resolve) => {
      const sample = this.detectionService.getBox(data.sample, data.workerId);

      const target = this.canvasService.convertBoxCoordinates({
        width: data.width,
        height: data.height,
        moveX: data.moveX,
        moveY: data.moveY,
      });

      const targetWithTolerance = data.tolerance
        ? addTargetTolerance(target, data.tolerance.x, data.tolerance.y)
        : target;

      const isConfirmed = this.getDebounce(
        isBoxInBox({
          detection: sample,
          target: targetWithTolerance,
          confidence: data.confidence,
        }) &&
          (data.additionalCondition ?? true),
        3,
      );

      if (!isConfirmed) {
        this.renderService.addStatic({
          id: 'sample',
          overlay: {
            type: 'rectangle',
            fillColor: OverlayColor.blue,
            transparency: 0.3,
            image: data.image,
            ...overlayCoordinates(target),
          },
          replace: { id: 'sampleConfirmed' },
        });
      } else {
        this.renderService.addStatic({
          id: 'sampleConfirmed',
          overlay: {
            type: 'rectangle',
            fillColor: OverlayColor.green,
            transparency: 0.3,
            image: 'overlays/check.svg',
            ...overlayCoordinates(target),
          },
          replace: { id: 'sample' },
        });
      }
      resolve(isConfirmed);
    });
  }

  checkInstrumentsPosition(
    data: {
      id: string;
      workerId: string;
      confidence: number;
      moveX?: number;
      moveY?: number;
      radius: number;
      image: string;
      additionalCondition?: boolean;
    }[],
  ): Promise<boolean> {
    return new Promise((resolve) => {
      const isConfirmed = data.every((entry) => {
        const detection = this.detectionService.getBox(
          entry.id,
          entry.workerId,
        );
        const target = {
          ...this.canvasService.convertCenterCoordinates({
            moveX: entry.moveX,
            moveY: entry.moveY,
          }),
          radius: entry.radius,
        };
        return isCenterInCircle({
          detection,
          target,
          confidence: entry.confidence,
        });
      });
      if (!isConfirmed) {
        data.forEach((entry) => {
          const target = {
            ...this.canvasService.convertCenterCoordinates({
              moveX: entry.moveX,
              moveY: entry.moveY,
            }),
            radius: entry.radius,
          };
          this.renderService.addStatic({
            id: entry.id,
            overlay: {
              type: 'circle',
              fillColor: OverlayColor.blue,
              transparency: 0.3,
              image: entry.image,
              ...target,
            },
          });
        });
      } else {
        data.forEach((entry) => {
          const target = {
            ...this.canvasService.convertCenterCoordinates({
              moveX: entry.moveX,
              moveY: entry.moveY,
            }),
            radius: entry.radius,
          };
          this.renderService.addStatic({
            id: entry.id + 'Confirmed',
            overlay: {
              type: 'circle',
              fillColor: OverlayColor.green,
              transparency: 0.3,
              image: 'overlays/check.svg',
              ...target,
            },
            replace: { id: entry.id, persist: true },
            timeout: 2000,
          });
        });
      }
      resolve(isConfirmed);
    });
  }
}
